import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useSyncExternalStore,
} from "react";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";

type SectionBodyLayout = { height: number; y: number };

/** Scroll geometry is external to React: a scroll must not render every accordion child. */
export function createSectionAccordionViewport() {
  let height = 0;
  let offset = 0;
  const bodies = new Map<string, SectionBodyLayout>();
  const listeners = new Set<() => void>();
  // All variable-height bodies resized in one commit must use the same pre-correction anchor.
  // Otherwise an earlier body's correction changes the offset seen by a later body's effect.
  let geometrySnapshot:
    | {
        bodies: ReadonlyMap<string, SectionBodyLayout>;
        height: number;
        key: string;
        offset: number;
      }
    | undefined;
  const publish = () => {
    for (const listener of listeners) {
      listener();
    }
  };

  return {
    getBody: (key: string) => bodies.get(key),
    getViewport: () => ({ height, offset }),
    readGeometryChangeSnapshot: (key: string) => {
      if (geometrySnapshot?.key !== key) {
        const snapshot = { bodies: new Map(bodies), height, key, offset };
        geometrySnapshot = snapshot;
        queueMicrotask(() => {
          if (geometrySnapshot === snapshot) {
            geometrySnapshot = undefined;
          }
        });
      }
      return geometrySnapshot;
    },
    retainBodies: (keys: ReadonlySet<string>) => {
      for (const key of bodies.keys()) {
        if (!keys.has(key)) {
          bodies.delete(key);
        }
      }
    },
    setBody: (key: string, layout: SectionBodyLayout) => {
      const previous = bodies.get(key);
      if (previous?.height === layout.height && previous.y === layout.y) {
        return;
      }
      bodies.set(key, layout);
      publish();
    },
    setViewport: (nextHeight: number, nextOffset = offset) => {
      if (height === nextHeight && offset === nextOffset) {
        return;
      }
      height = nextHeight;
      offset = nextOffset;
      publish();
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const SectionAccordionViewportContext = createContext<ReturnType<
  typeof createSectionAccordionViewport
> | null>(null);
export const SectionAccordionBodyKeyContext = createContext<string | null>(null);

/**
 * Content-relative midpoints between rows, shared by sheet snapping and row windowing. The row
 * pitch lets a sheet keep snapping where further rows would sit when the content ends early.
 */
export const SectionAccordionRowBoundariesContext = createContext<
  ((boundaries: readonly number[], rowPitch: number) => void) | null
>(null);

const INITIAL_ROW_COUNT = 12;
const subscribeToNothing = () => () => {};

/**
 * Window fixed-height rows inside a section's existing native scroll body. The caller keeps the
 * omitted rows' exact height as spacers, so headers and scroll position never depend on which
 * rows happen to be mounted. `offsetTop` is the first row's offset within the section body.
 *
 * This is deliberately separate from native sticky-header positioning. A nested scrollable list
 * cannot share the accordion's viewport, and virtualizing its headers removes the markers needed
 * by the native top AND bottom header stacks. Only the expensive body rows are windowed here.
 */
export function useSectionAccordionRowWindow({
  active = true,
  enabled = true,
  offsetTop = 0,
  rowCount,
  rowGap = 0,
  rowHeight,
}: {
  /** Release rows without changing their body's spacers or saved scroll geometry. */
  active?: boolean;
  /** Enable viewport windowing; false retains all rows for non-accordion callers. */
  enabled?: boolean;
  offsetTop?: number;
  rowCount: number;
  /** Space between the cards, already included in rowHeight. */
  rowGap?: number;
  /** Distance from one row's top to the next, including the gap between them. */
  rowHeight: number;
}) {
  const isNavigationFocused = useIsNavigationFocused();
  const isActive = active && isNavigationFocused;
  const viewport = useContext(SectionAccordionViewportContext);
  const bodyKey = useContext(SectionAccordionBodyKeyContext);
  const reportBoundaries = useContext(SectionAccordionRowBoundariesContext);
  useLayoutEffect(() => {
    if (isActive) {
      reportBoundaries?.(
        Array.from(
          { length: rowCount },
          (_, index) => offsetTop + (index + 1) * rowHeight - rowGap / 2,
        ),
        rowHeight,
      );
    }
  }, [isActive, offsetTop, reportBoundaries, rowCount, rowGap, rowHeight]);
  const getSnapshot = useCallback(() => {
    if (!isActive) {
      return "0:0";
    }
    if (!enabled || viewport === null || bodyKey === null || rowHeight <= 0) {
      return `0:${rowCount}`;
    }
    const { height, offset } = viewport.getViewport();
    const body = viewport.getBody(bodyKey);
    if (body === undefined || height <= 0) {
      // Before native layout, keep the first commit bounded too. The spacer already reserves the
      // entire body's height; native header navigation can therefore jump before rows are ready.
      return `0:${Math.min(INITIAL_ROW_COUNT, rowCount)}`;
    }
    const relativeOffset = offset - body.y - offsetTop;
    const start = Math.max(
      0,
      Math.min(rowCount, Math.floor((relativeOffset - height) / rowHeight)),
    );
    const end = Math.max(
      start,
      Math.min(rowCount, Math.ceil((relativeOffset + height * 2) / rowHeight)),
    );
    // Primitive snapshots compare equal during movement inside the same row window. Every scroll
    // updates the geometry, but React only renders when rows enter or leave the overscan boundary.
    return `${start}:${end}`;
  }, [bodyKey, enabled, isActive, offsetTop, rowCount, rowHeight, viewport]);
  const snapshot = useSyncExternalStore(
    enabled && isActive ? (viewport?.subscribe ?? subscribeToNothing) : subscribeToNothing,
    getSnapshot,
    getSnapshot,
  );
  const [start = 0, end = rowCount] = snapshot.split(":").map(Number);
  return { end, start };
}
